import type { PipelineFilters } from '@/modules/pipelines/contexts/pipeline-notification-context';
import { Page } from '@/shared/pagination/export';
import {
  Notice,
  NoticeHistory,
  FullNotice,
} from '@/modules/pipelines/model/pipeline-alerts/pipeline-alert';
import { PipelineAsset } from '@/modules/pipelines/model/pipeline-alerts/pipeline-asset';
import { PipelineEventType } from '@/modules/pipelines/model/pipeline-alerts/pipeline-event-type';
import { PetroPipelineNoticePage } from '@/modules/pipelines/model/pipeline-alerts/petro-pipeline-notice';
import axios from 'axios';
import { EntityNode } from '@/modules/pipelines/model/pipeline-alerts/entity-node';

function toBackendParams(filters: PipelineFilters, eventTypes?: PipelineEventType[]) {
  const { eventTypeClassification: eventType, event, ...rest } = filters;

  let typeIds: string[] | undefined;
  if (eventType?.length) {
    const groupToTypes = new Map<string, string[]>();
    eventTypes
      ?.filter(t => t.scheme === 'type')
      .forEach(t => {
        if (!groupToTypes.has(t.parentIds[0])) groupToTypes.set(t.parentIds[0], []);
        groupToTypes.get(t.parentIds[0])?.concat(t._id);
      });
    const out = new Set<string>();
    eventType.forEach(id => {
      if (id.startsWith('group:')) groupToTypes.get(id)?.forEach(t => out.add(t));
      else out.add(id);
    });
    typeIds = out.size ? Array.from(out) : undefined;
  }

  return { ...rest, eventTypeClassification: typeIds };
}

export const MINIMAL_PIPELINE_HISTORY_SIZE = 10;
async function getMinimalPipelineHistory(
  page: number,
  filters: PipelineFilters,
  eventTypes?: PipelineEventType[],
): Promise<Page<NoticeHistory>> {
  const params = {
    page,
    pageSize: MINIMAL_PIPELINE_HISTORY_SIZE,
    ...toBackendParams(filters, eventTypes),
  };
  return (
    await axios.get(`/api/petro-pipeline/notice/history`, {
      params,
      paramsSerializer: { indexes: null },
    })
  ).data;
}

async function getNoticesByEntities(entityIds: string[], sinceIso?: string): Promise<Notice[]> {
  if (!entityIds.length) return [];
  const res = await axios.get(`/api/petro-pipeline/notice`, {
    params: {
      relatedEntities: entityIds,
      pageSize: 500,
      ...(sinceIso ? { 'postedBetween[start]': sinceIso } : {}),
    },
    paramsSerializer: { indexes: null },
  });
  return res.data?.content ?? [];
}

async function getPipelineEventHistory(id: string, page: number): Promise<Page<Notice>> {
  return (
    await axios.get(`/api/petro-pipeline/notice/history/${id}`, {
      params: { page, pageSize: 10 },
    })
  ).data;
}

async function getFullPipelineAlert(id: string): Promise<FullNotice> {
  return (await axios.get(`/api/petro-pipeline/notice/${id}`)).data.content;
}

// async function getPipelineOperators(page = 0, pageSize = 100): Promise<Page<PipelineOperator>> {
//   return (
//     await axios.get(`/api/petro-pipeline/operator`, {
//       params: { page, pageSize },
//     })
//   ).data;
// }

async function getPipelineAssets(): Promise<Page<PipelineAsset>> {
  return (await axios.get(`/api/petro-pipeline/asset`)).data;
}

// const USE_ENTITY = process.env.REACT_APP_USE_ENTITY === 'true';

// async function getPipelineRegions(): Promise<PipeRegion[]> {
//   const endpoint = USE_ENTITY
//     ? `/api/petro-pipeline/entity/graph`
//     : `/api/petro-pipeline/region/graph`;

//   return (await axios.get(endpoint)).data;
// }

async function getPetroPipelineNotices(
  page: number,
  filters: PipelineFilters,
  pageSize = 10,
): Promise<PetroPipelineNoticePage> {
  return (
    await axios.get(`/api/petro-pipeline/notice`, {
      params: { page, pageSize, ...filters },
    })
  ).data;
}

async function getPipelineEventTypes(page = 0, pageSize = 100): Promise<Page<PipelineEventType>> {
  const content = (
    await axios.get(`/api/petro-pipeline/event-type`, {
      params: { page, pageSize },
    })
  ).data;

  return { ...(content.content !== undefined ? content : { content: content }), totalPages: 1 };
}

async function getPipelineEntities(types: string[]) {
  return (
    await axios.get(`/api/petro-pipeline/entity`, {
      params: { types },
    })
  ).data;
}

async function getPipelineEntityById(id: string): Promise<EntityNode> {
  return (await axios.get(`/api/petro-pipeline/entity/${id}`, {})).data;
}

async function getPlanningStatuses(): Promise<Page<{ _id: string }>> {
  return (await axios.get(`/api/petro-pipeline/notice/planning-status`)).data;
}

async function getEventStatuses(): Promise<Page<{ _id: string }>> {
  return (await axios.get(`/api/petro-pipeline/notice/event-status`)).data;
}

const api = {
  // getMinimalPipelineAlerts,
  getMinimalPipelineHistory,
  getNoticesByEntities,
  getPipelineEventHistory,
  getFullPipelineAlert,
  // getPipelineOperators,
  getPipelineAssets,
  // getPipelineRegions,
  getPipelineEventTypes,
  getPetroPipelineNotices,
  getPipelineEntities,
  getPipelineEntityById,
  getPlanningStatuses,
  getEventStatuses,
};

export default api;
